import { useMemo } from 'react';
import gql from 'graphql-tag';
import { useQuery, ApolloError } from '@apollo/client';

type Impact = GetImpactStatsQuery['plan']['impact'];
type Gas = Impact['fuelTypeEquivalency']['naturalGas'];

export type FuelTypeEquivalency = Impact['fuelTypeEquivalency'] & { gas: Gas };
interface ImpactStats {
  createdAt: GetImpactStatsQuery['plan']['createdAt'];
  carbonEquivalency: Impact['carbonEquivalency'];
  fuelTypeEquivalency: FuelTypeEquivalency;
}
interface Props {
  startDate?: StringRangeFilter | null;
  endDate?: StringRangeFilter | null;
  skip?: boolean;
  planId?: number;
}
export const useImpactStats = ({
  startDate = null,
  endDate = null,
  skip,
  planId,
}: Props): { data?: ImpactStats; loading?: boolean; error?: ApolloError } => {
  const { data, loading, error } = useQuery<
    GetImpactStatsQuery,
    GetImpactStatsQueryVariables
  >(GET_IMPACT_STATS, {
    skip: skip || !planId,
    variables: {
      planId: planId || -1,
      endDate,
      startDate,
    },
  });

  const impactStats = useMemo(() => {
    if (!data) return data;
    const {
      plan: { impact, createdAt },
    } = data;
    return {
      createdAt,
      carbonEquivalency: impact.carbonEquivalency,
      fuelTypeEquivalency: {
        ...impact.fuelTypeEquivalency,
        gas: impact.fuelTypeEquivalency.naturalGas,
      },
    };
  }, [data]);

  return { data: impactStats, error, loading };
};

export const mockImpactStats = {
  carbonEquivalency: {
    kwh: 9999,
    totalCarbon: 999,
    totalRecsPurchased: 99,
  },
  fuelTypeEquivalency: {
    coal: {
      equivalency: 20,
      units: 'lbs',
    },
    naturalGas: {
      equivalency: 20,
      units: 'lbs',
    },
    nuclear: {
      equivalency: 20,
      units: 'lbs',
    },
    oil: {
      equivalency: 20,
      units: 'lbs',
    },
  },
};

export const useImpactStatsMock = {
  data: {
    createdAt: '2020-04-12T04:00:00.000Z',
    ...mockImpactStats,
  },
  error: null,
  loading: false,
};

export const GET_IMPACT_STATS = gql`
  query GetImpactStats(
    $planId: Int!
    $startDate: StringRangeFilter
    $endDate: StringRangeFilter
  ) {
    plan(id: $planId) {
      id
      createdAt
      impact {
        carbonEquivalency(startDate: $startDate, endDate: $endDate) {
          totalCarbon
          kwh
          totalRecsPurchased
        }
        fuelTypeEquivalency(startDate: $startDate, endDate: $endDate) {
          coal {
            equivalency
            units
          }
          naturalGas {
            equivalency
            units
          }
          nuclear {
            equivalency
            units
          }
          oil {
            equivalency
            units
          }
        }
      }
    }
  }
`;

export const getImpactStatsMock = {
  plan: {
    id: 1,
    createdAt: '2020-04-12T04:00:00.000Z',
    impact: mockImpactStats,
  },
};
